/**
 * Attribution Tracking System
 * Captures marketing attribution parameters and saves them to cart for order attribution
 *
 * Tracks:
 * - Google Ads (gclid)
 * - Facebook/Instagram (fbclid)
 * - TikTok (ttclid)
 * - Microsoft Ads (msclkid)
 * - UTM Parameters (utm_source, utm_medium, utm_campaign, utm_content, utm_term)
 * - Referrer
 *
 * @version 1.0.0
 * @date 2025-11-04
 */

(function () {
  'use strict';

  // Configuration
  const CONFIG = {
    storageKey: 'coracrea_attribution',
    quizStorageKey: 'coracrea_quiz_attribution',
    expiryDays: 30, // Attribution window
    updateCartUrl: '/cart/update.js',
    debug: false, // Set to true for console logging
  };

  /**
   * Main tracking object
   */
  const AttributionTracker = {
    _listeners: {
      domContentLoaded: null,
      cartUpdated: null,
    },
    /**
     * Initialize tracking on page load
     */
    init() {
      try {
        this.log('Attribution Tracker initialized');

        // Capture current parameters
        const attribution = this.captureAttribution();

        // Save to storage if we have new data
        if (attribution && Object.keys(attribution).length > 0) {
          this.saveAttribution(attribution);
          this.log('New attribution captured:', attribution);
        }

        // Update cart attributes
        this.updateCartAttributes();
      } catch (error) {
        this.error('Initialization error:', error);
      }
    },

    /**
     * Capture attribution from current URL and referrer
     */
    captureAttribution() {
      const params = new URLSearchParams(window.location.search);
      const attribution = {};

      // Click IDs (highest priority - overrides everything)
      if (params.has('gclid')) {
        attribution._gclid = params.get('gclid');
        attribution._source = 'Google Ads';
        attribution._medium = 'cpc';
      } else if (params.has('fbclid')) {
        attribution._fbclid = params.get('fbclid');
        attribution._source = 'Facebook';
        attribution._medium = 'social';
      } else if (params.has('ttclid')) {
        attribution._ttclid = params.get('ttclid');
        attribution._source = 'TikTok';
        attribution._medium = 'social';
      } else if (params.has('msclkid')) {
        attribution._msclkid = params.get('msclkid');
        attribution._source = 'Microsoft Ads';
        attribution._medium = 'cpc';
      } else if (params.has('li_fat_id')) {
        attribution._li_fat_id = params.get('li_fat_id');
        attribution._source = 'LinkedIn';
        attribution._medium = 'social';
      } else if (params.has('epik')) {
        attribution._epik = params.get('epik');
        attribution._source = 'Pinterest';
        attribution._medium = 'social';
      }

      // UTM Parameters (used if no click ID)
      if (params.has('utm_source')) {
        if (!attribution._source) attribution._source = params.get('utm_source');
        attribution._utm_source = params.get('utm_source');

        // Special handling for gift quiz
        if (params.get('utm_source') === 'gift-quiz') {
          attribution._quiz_source = 'Gift-quiz';
        }
      }
      if (params.has('utm_medium')) {
        if (!attribution._medium) attribution._medium = params.get('utm_medium');
        attribution._utm_medium = params.get('utm_medium');
      }
      if (params.has('utm_campaign')) {
        attribution._campaign = params.get('utm_campaign');
        attribution._utm_campaign = params.get('utm_campaign');
      }
      if (params.has('utm_content')) {
        attribution._utm_content = params.get('utm_content');
      }
      if (params.has('utm_term')) {
        attribution._utm_term = params.get('utm_term');
      }

      // Capture referrer (only if no click ID or UTM)
      if (document.referrer && !attribution._source) {
        try {
          const referrerUrl = new URL(document.referrer);
          const referrerHost = referrerUrl.hostname.toLowerCase();

          // Only capture external referrers (not same domain)
          if (!referrerHost.includes(window.location.hostname)) {
            attribution._referrer = referrerHost;

            // Identify common referrers
            if (referrerHost.includes('google')) {
              attribution._source = 'Google';
              attribution._medium = 'organic';
            } else if (referrerHost.includes('facebook') || referrerHost.includes('fb.com')) {
              attribution._source = 'Facebook';
              attribution._medium = 'social';
            } else if (referrerHost.includes('instagram')) {
              attribution._source = 'Instagram';
              attribution._medium = 'social';
            } else if (referrerHost.includes('pinterest')) {
              attribution._source = 'Pinterest';
              attribution._medium = 'social';
            } else if (referrerHost.includes('tiktok')) {
              attribution._source = 'TikTok';
              attribution._medium = 'social';
            } else if (referrerHost.includes('twitter') || referrerHost.includes('t.co')) {
              attribution._source = 'Twitter';
              attribution._medium = 'social';
            } else if (referrerHost.includes('linkedin')) {
              attribution._source = 'LinkedIn';
              attribution._medium = 'social';
            } else if (referrerHost.includes('bing') || referrerHost.includes('yahoo')) {
              attribution._source = 'Search Engine';
              attribution._medium = 'organic';
            }
          }
        } catch (e) {
          this.log('Error parsing referrer:', e);
        }
      }

      // Add timestamp
      if (Object.keys(attribution).length > 0) {
        attribution._timestamp = new Date().toISOString();
      }

      return attribution;
    },

    /**
     * Save attribution to localStorage
     */
    saveAttribution(attribution) {
      try {
        const existing = this.getStoredAttribution();

        // Merge with existing, new data takes precedence
        const merged = { ...existing, ...attribution };

        // Add expiry
        merged._expiry = Date.now() + CONFIG.expiryDays * 24 * 60 * 60 * 1000;

        localStorage.setItem(CONFIG.storageKey, JSON.stringify(merged));
        this.log('Attribution saved to localStorage');
      } catch (error) {
        this.error('Storage error:', error);
      }
    },

    /**
     * Get stored attribution from localStorage
     */
    getStoredAttribution() {
      try {
        const stored = localStorage.getItem(CONFIG.storageKey);
        if (!stored) return {};

        const attribution = JSON.parse(stored);

        // Check expiry
        if (attribution._expiry && Date.now() > attribution._expiry) {
          this.log('Attribution expired, clearing');
          localStorage.removeItem(CONFIG.storageKey);
          return {};
        }

        return attribution;
      } catch (error) {
        this.error('Storage read error:', error);
        return {};
      }
    },

    /**
     * Get quiz attribution from localStorage
     */
    getQuizAttribution() {
      try {
        const stored = localStorage.getItem(CONFIG.quizStorageKey);
        if (!stored) return {};

        const quizAttribution = JSON.parse(stored);
        this.log('Quiz attribution found:', quizAttribution);
        return quizAttribution;
      } catch (error) {
        this.error('Quiz attribution read error:', error);
        return {};
      }
    },

    /**
     * Clear quiz attribution after it's been used
     */
    clearQuizAttribution() {
      try {
        localStorage.removeItem(CONFIG.quizStorageKey);
        this.log('Quiz attribution cleared');
      } catch (error) {
        this.log('Error clearing quiz attribution:', error);
      }
    },

    /**
     * Update cart attributes with attribution data
     */
    async updateCartAttributes() {
      try {
        const attribution = this.getStoredAttribution();
        const quizAttribution = this.getQuizAttribution();

        // Merge both attributions
        const mergedAttribution = { ...attribution, ...quizAttribution };

        if (!mergedAttribution || Object.keys(mergedAttribution).length === 0) {
          this.log('No attribution to update cart with');
          return;
        }

        // Prepare form data for cart attributes (Shopify requires form-urlencoded format)
        const formData = new FormData();

        // Add all attribution data as cart attributes
        // Remove internal fields (_expiry, _timestamp for cart)
        Object.keys(mergedAttribution).forEach((key) => {
          if (!key.startsWith('_expiry')) {
            formData.append(`attributes[${key}]`, mergedAttribution[key]);
          }
        });

        // Update cart via AJAX using form-urlencoded format
        const response = await fetch(CONFIG.updateCartUrl, {
          method: 'POST',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: formData,
        });

        if (response.ok) {
          this.log('Cart attributes updated successfully');
          // Clear quiz attribution after successful cart update
          if (Object.keys(quizAttribution).length > 0) {
            this.clearQuizAttribution();
          }
        } else {
          this.log('Cart update failed:', response.status);
        }
      } catch (error) {
        // Silently fail - cart update is not critical
        this.log('Cart update error:', error);
      }
    },

    /**
     * Debug logging
     */
    log(...args) {
      if (CONFIG.debug) {
        // eslint-disable-next-line no-console
        console.log('[Attribution Tracker]', ...args);
      }
    },

    error(...args) {
      if (CONFIG.debug) {
        console.error('[Attribution Tracker]', ...args);
      }
    },

    /**
     * Clear attribution (for testing)
     */
    clear() {
      localStorage.removeItem(CONFIG.storageKey);
      this.log('Attribution cleared');
    },

    /**
     * Get current attribution (for debugging)
     */
    getAttribution() {
      return this.getStoredAttribution();
    },

    setupListeners() {
      if (document.readyState === 'loading') {
        this._listeners.domContentLoaded = () => this.init();
        document.addEventListener('DOMContentLoaded', this._listeners.domContentLoaded);
      } else {
        this.init();
      }
      this._listeners.cartUpdated = () => {
        this.log('Cart updated event detected');
        this.updateCartAttributes();
      };
      document.addEventListener('cart:updated', this._listeners.cartUpdated);
    },

    cleanup() {
      if (this._listeners.domContentLoaded) {
        document.removeEventListener('DOMContentLoaded', this._listeners.domContentLoaded);
        this._listeners.domContentLoaded = null;
      }
      if (this._listeners.cartUpdated) {
        document.removeEventListener('cart:updated', this._listeners.cartUpdated);
        this._listeners.cartUpdated = null;
      }
      this.log('Event listeners cleaned up');
    },
  };

  // Initialize on page load with proper listener setup
  AttributionTracker.setupListeners();

  // Cleanup listeners on page unload to prevent memory leaks
  window.addEventListener('beforeunload', () => {
    AttributionTracker.cleanup();
  });

  // Expose to window for debugging
  window.AttributionTracker = AttributionTracker;
})();
